import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { LANGUAGES, SUPPORTED_CODES, DEFAULT_LANG } from './languages';

import ko from './translations/ko';
import en from './translations/en';
import zh from './translations/zh';
import ja from './translations/ja';
import th from './translations/th';
import fr from './translations/fr';
import de from './translations/de';
import es from './translations/es';

const translations = { ko, en, zh, ja, th, fr, de, es };

const STORAGE_KEY = 'ms_lang';

function detectLanguage() {
  // 1. localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_CODES.includes(stored)) return stored;

  // 2. navigator.language
  const browserLang = (navigator.language || '').slice(0, 2).toLowerCase();
  if (SUPPORTED_CODES.includes(browserLang)) return browserLang;

  // 3. default
  return DEFAULT_LANG;
}

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = useCallback((code) => {
    if (SUPPORTED_CODES.includes(code)) {
      setLangState(code);
      localStorage.setItem(STORAGE_KEY, code);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ lang, setLang, meta, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

export function useTranslation() {
  const { lang } = useLanguage();
  const t = useCallback(
    (key, params) => {
      let str = translations[lang]?.[key] || translations[DEFAULT_LANG]?.[key] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        });
      }
      return str;
    },
    [lang]
  );
  return { t, lang };
}

export function useLocalizedData() {
  const { lang } = useLanguage();
  const localize = useCallback(
    (field) => {
      if (!field) return '';
      if (typeof field === 'string') return field;
      return field[lang] || field[DEFAULT_LANG] || field.en || Object.values(field)[0] || '';
    },
    [lang]
  );
  return { localize, lang };
}
